import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { FormatDateService } from '../../../helpers/format-date.service';

@Component({
    selector: 'app-input-with-checkbox',
    imports: [CommonModule, FormsModule, ReactiveFormsModule],
    templateUrl: './input-with-checkbox.component.html',
    styleUrl: './input-with-checkbox.component.css'
})
export class InputWithCheckboxComponent implements OnInit, OnDestroy {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @Input() disableFields: any;
  @Input() autoFocused: any;
  @Input() labelAbove: any;
  @Input() twoGridColumn: any;
  /** Allows parent to dynamically mark this field as required (shows asterisk) */
  @Input() dynamicRequired: boolean = false;
  
  // Custom inputs for the checkbox
  @Input() checkboxControlName: string = '';
  @Input() checkboxLabel: string = '';

  errors: { [key: string]: string } = {};
  autoSetStart = false;
  autoSetEnd = false;
  isFocused = false;
  @ViewChild('focusInput') focusInput!: ElementRef<HTMLInputElement>;
  private availabilitySub?: Subscription;

  constructor(
    private formService: FormsServiceService,
    private formatDateService: FormatDateService
  ) {}

  ngOnInit(): void {
    const startControl = this.formGroup.get('startDate');

    if (startControl) {
      startControl.valueChanges.subscribe(() => {
        if (this.fields?.fieldName?.toLowerCase() === 'duedate') {
          this.onKeyUpValidateFields(this.fields);
        }
      });
    }
  }

  ngOnChanges() {
    if (!this.autoFocused || this.disableFields) return;

    setTimeout(() => {
      const element = this.focusInput?.nativeElement;

      if (element && !element.disabled && element.offsetParent !== null) {
        element.focus();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.availabilitySub) {
      this.availabilitySub.unsubscribe();
    }
  }

  formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }
  
  get formattedDateValue(): string {
    const val = this.formGroup?.get(this.fields?.fieldName)?.value;
    return val ? this.formatDateService.formatDisplayDate(val) : '';
  }

  get control() {
    return this.formGroup?.get(this.fields?.fieldName);
  }

  /** Function to show the validation message on enter of the input fields */
  onKeyUpValidateFields(field: any): void {
    if (!field) return;
    const fieldName = field.fieldName;
    const control = this.formGroup.get(fieldName);
    if (!control) return;
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
    this.errors[fieldName] = '';
    
    if (control.hasError('minlength')) {
      const minLengthRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'minlength',
      );
      this.errors[fieldName] =
        minLengthRule?.error ||
        `Minimum length is ${control.errors?.['minlength']?.requiredLength}.`;
      return;
    }

    if (control.hasError('maxlength')) {
      const maxLengthRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'maxlength',
      );
      this.errors[fieldName] =
        maxLengthRule?.error ||
        `Maximum length is ${control.errors?.['maxlength']?.requiredLength}.`;
      return;
    }

    if (control.hasError('pattern')) {
      const regexRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'regex',
      );
      this.errors[fieldName] =
        `Please enter valid ${this.formatDisplayName(field.displayName || fieldName)}` ||
        'Invalid format.';

      return;
    }

    if (control.hasError('email')) {
      this.errors[fieldName] = 'Enter a valid email address.';
      return;
    }

    if (control.hasError('min')) {
      const minRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'min',
      );
      this.errors[fieldName] =
        minRule?.error || `Minimum value is ${control.errors?.['min']?.min}.`;
      return;
    }

    if (control.hasError('max')) {
      const maxRule = field.validationRules?.find(
        (v: any) => v.type.toLowerCase() === 'max',
      );
      this.errors[fieldName] =
        maxRule?.error || `Maximum value is ${control.errors?.['max']?.max}.`;
      return;
    }

    const dayAfterRule = field.validationRules?.find(
      (v: any) => v.type.toLowerCase() === 'dateafter',
    );
    if (
      dayAfterRule &&
      (fieldName.toLowerCase() === 'duedate' ||
        fieldName.toLowerCase() === 'startdate')
    ) {
      const startDateValue = this.formGroup.get('startDate')?.value;
      const dueDateControl = this.formGroup.get('dueDate');
      const dueDateValue = dueDateControl?.value;

      if (startDateValue && dueDateValue) {
        const startDate = new Date(startDateValue);
        const dueDate = new Date(dueDateValue);

        if (dueDate < startDate) {
          this.errors['dueDate'] =
            dayAfterRule.error || `End Date cannot be earlier than Start Date.`;
          dueDateControl?.setErrors({ dateAfter: true });
          return;
        } else {
          // ✅ Clear error if valid now
          if (dueDateControl?.hasError('dateAfter')) {
            const newErrors = { ...dueDateControl.errors };
            delete newErrors['dateAfter'];
            dueDateControl.setErrors(
              Object.keys(newErrors).length ? newErrors : null,
            );
          }
        }
      }
    }

    this.errors[fieldName] = '';

    const availabilityDep = field.fieldDependencies?.find(
      (dep: any) => dep.trigger === 'availabilityCheck',
    );

    let availabilityConfig: any = null;
    if (availabilityDep) {
      for (const condBlock of availabilityDep.condition || []) {
        const action = condBlock.actions?.find(
          (act: any) => act.actionType === 'availability',
        );
        if (action) {
          availabilityConfig = {};
          (action.value || []).forEach((item: any) => {
            if (item && item.Key && item.Value !== undefined) {
              availabilityConfig[item.Key] = item.Value;
            }
          });
          break;
        }
      }
    }

    if (
      availabilityConfig ||
      ((field.formId === '6a0edb062db4b8fda5f347ad' || field.formId == '6a0edb612db4b8fda5f347b2') && fieldName === 'role')
    ) {
      const minLength = availabilityConfig ? parseInt(availabilityConfig.minLength, 10) || 3 : 3;
      const targetFields = availabilityConfig?.fields
        ? (Array.isArray(availabilityConfig.fields) ? availabilityConfig.fields : [availabilityConfig.fields])
        : [fieldName];
      const formId = availabilityConfig?.formId || field.formId;
      const val = control.value;

      if (val && val.trim().length >= minLength) {
        const payload = {
          formId: formId,
          fields: targetFields,
          value: val.trim(),
        };
        if (this.availabilitySub) {
          this.availabilitySub.unsubscribe();
        }
        this.availabilitySub = this.formService
          .fieldAvailabilityChecker(payload)
          .subscribe({
            next: (res: any) => {
              if (res.success) {
                if (control.hasError('availability')) {
                  const newErrors = { ...control.errors };
                  delete newErrors['availability'];
                  control.setErrors(
                    Object.keys(newErrors).length ? newErrors : null,
                  );
                }
                this.errors[fieldName] = '';
              }
            },
            error: (err) => {
              if (err.error?.data?.exists) {
                this.errors[fieldName] =
                  err.error.message || `${this.formatDisplayName(fieldName)} already exists`;
                control.setErrors({ availability: true });
              }
            },
          });
      } else {
        if (control.hasError('availability')) {
          const newErrors = { ...control.errors };
          delete newErrors['availability'];
          control.setErrors(Object.keys(newErrors).length ? newErrors : null);
        }
      }
    }
  }

  formatDisplayName(value: string): string {
    if (!value) return '';

    const uppercaseWords = ['api', 'url', 'id', 'ip', 'dns', 'sso', 'saml', 'oauth', 'tls', 'ssl'];
    const originalWords = value.split(' ');

    return value
      .toLowerCase()
      .split(' ')
      .map((word, index) => {
        const originalWord = originalWords[index];
        if (originalWord && originalWord === originalWord.toUpperCase() && isNaN(Number(originalWord))) {
          return originalWord;
        }
        const cleanWord = word.replace(/[^a-z0-9]/g, '');
        if (uppercaseWords.includes(cleanWord)) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }
}
